import React, { useState } from 'react';
import {
    DialogContent,
    Button,
    Stack,
    Typography,
    Box,
    IconButton,
    Chip,
    List,
    ListItem,
    Tooltip,
} from '@mui/material';
import { PurchaseOrderItem, PurchaseOrderRecommendation } from '@domain/purchasing';
import { Shipment } from '@domain/shipment';
import { differenceInWeeks, addDays, differenceInDays, format, startOfDay } from 'date-fns';
import {
    Close as CloseIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import EditableDateCell from '@shared/ui/common/EditableDateCell';
import { useTheme } from '@mui/material';
import { ProductDisplay } from '@features/shipments/components/shared';
import ForecastWaterfallChart from './ForecastWaterfallChart';
import ShipmentPreview from '@features/purchasing/components/preview/ShipmentPreview';
import { ForecastPreview } from '@features/products/components/forecast-preview';
import { BorderedBlock } from '@shared/ui/common/BorderedBlock';
import { useNavigate } from 'react-router-dom';
import { useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';
import { useGenericDrawer } from '@shared/hooks';

interface PurchaseOrderItemDetailsProps {
    item: PurchaseOrderItem;
    orderDate?: Date;
    shipment?: Shipment;
    availableShipments: Shipment[];
    recommendation?: PurchaseOrderRecommendation;
    onClose: () => void;
    onRemove: (item: PurchaseOrderItem) => void;
    onShipmentChange: (item: PurchaseOrderItem, newShipment: Shipment) => void;
    onItemUpdate?: (item: PurchaseOrderItem) => void;
}

export const PurchaseOrderItemDetails: React.FC<PurchaseOrderItemDetailsProps> = ({
    item,
    shipment,
    availableShipments,
    recommendation,
    orderDate,
    onClose,
    onRemove,
    onShipmentChange,
    onItemUpdate,
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [selectedShipment, setSelectedShipment] = useState<Shipment | undefined>(shipment);
    const [currentItem, setCurrentItem] = useState<PurchaseOrderItem>(item);

    const { closeBottomSheet } = useBottomSheet();
    const { closeDrawer } = useGenericDrawer();

    const [productionStartDate, setProductionStartDate] = useState<Date>(item.productionStartDate || new Date());
    const [productionReadyDate, setProductionReadyDate] = useState<Date>(item.productionReadyDate || new Date());
    const [eta, setEta] = useState<Date>(item.eta || new Date());

    const updateProductionStartDate = (date: Date | undefined) => {
        const newProductionReadyDate = date ? addDays(date, differenceInDays(productionReadyDate, productionStartDate)) : undefined;
        updateProductionReadyDate(newProductionReadyDate);

        setProductionStartDate(date || new Date());
        onItemUpdate?.({ ...currentItem, productionStartDate: date || new Date() });
    };

    const updateEta = (date: Date | undefined) => {
        setEta(date || new Date());
        onItemUpdate?.({ ...currentItem, eta: date || new Date() });
    };

    const updateProductionReadyDate = (date: Date | undefined) => {
        setProductionReadyDate(date || new Date());
        onItemUpdate?.({ ...currentItem, productionReadyDate: date || new Date() });
    };

    // Update currentItem when item prop changes
    React.useEffect(() => {
        setCurrentItem(item);
    }, [item]);


    const handleRemove = () => {
        onRemove(currentItem);
        onClose();
    };

    const handleShipmentChange = (newShipment: Shipment) => {
        onShipmentChange(currentItem, newShipment);
        setSelectedShipment(newShipment);
    };

    const handleViewInTimeTravel = (productId: string) => {
        navigate(`/timetravel/products/${productId}`);
        closeBottomSheet();
        closeDrawer();
    };

    return (
        <>
            <DialogHeader>
                <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                    <Typography variant="h6" noWrap>
                        Item details
                    </Typography>

                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogHeader>



            <DialogContent sx={{ p: 4 }}>

                <Stack spacing={2}>

                    {/* Current Shipment */}
                    <ProductDisplay
                        purchaseOrderItems={[currentItem]}
                    />

                    {/* Forecast */}
                    {currentItem.product?.id && (
                        <BorderedBlock header="Forecast">
                            <ForecastPreview
                                productId={currentItem.product.id}
                                forecastGradeFallback={currentItem.product.forecastGrade}
                                onViewInTimeTravel={() => handleViewInTimeTravel(currentItem.product.id)}
                            />
                        </BorderedBlock>
                    )}

                    {/* Production Dates */}
                    <Box
                        sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            p: 2,
                            bgcolor: theme.palette.background.default,
                        }}
                    >
                        <Typography variant="subtitle2">Production Schedule</Typography>

                        <List>
                            <ListItem
                                divider
                                sx={{ justifyContent: 'space-between', p: 0 }}
                            >
                                <Typography variant="body2" color="text.primary">Order Date</Typography>

                                <Box p={2}>
                                    <Typography variant="body2" color="text.secondary">
                                        {orderDate ? format(orderDate, 'EEE MMM dd') : 'N/A'}
                                    </Typography>
                                </Box>
                            </ListItem>

                            <ListItem
                                divider
                                sx={{ justifyContent: 'space-between', p: 0 }}
                            >
                                <Typography variant="body2" color="text.primary">Production Start Date</Typography>

                                <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0}>
                                    <Typography variant="body2" color="text.secondary">
                                        {currentItem.productionStartDate && orderDate
                                            ? `+ ${differenceInDays(startOfDay(productionStartDate), startOfDay(orderDate || new Date()))} days`
                                            : '0 days'
                                        }
                                    </Typography>

                                    <EditableDateCell
                                        value={productionStartDate}
                                        onValueChange={updateProductionStartDate}
                                        maxDate={currentItem.productionReadyDate}
                                    />
                                </Stack>
                            </ListItem>


                            <ListItem
                                sx={{ justifyContent: 'space-between', p: 0 }}
                            >
                                <Typography variant="body2" color="text.primary">Production Ready Date</Typography>


                                <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0}>

                                    <Typography variant="body2" color="text.secondary">
                                        {currentItem.productionStartDate && currentItem.productionReadyDate
                                            ? `+ ${differenceInDays(startOfDay(productionReadyDate), startOfDay(productionStartDate))} days`
                                            : '0 days'
                                        }
                                    </Typography>

                                    <EditableDateCell
                                        value={productionReadyDate}
                                        onValueChange={updateProductionReadyDate}
                                        minDate={currentItem.productionStartDate}
                                    />
                                </Stack>
                            </ListItem>

                            <ListItem
                                sx={{ justifyContent: 'space-between', p: 0 }}
                            >
                                <Typography variant="body2" color="text.primary">ETA</Typography>

                                <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.5}>

                                    <Tooltip title="Shipping Time">
                                        <Typography variant="body2" color="text.secondary">
                                            {currentItem.product.defaultShipmentMethod?.duration || 0} +
                                        </Typography>
                                    </Tooltip>

                                    <Tooltip title="Processing Time">
                                        <Typography variant="body2" color="text.secondary">
                                            {currentItem.product.processingDuration || 0} =
                                        </Typography>
                                    </Tooltip>

                                    <Tooltip title="Total Lead Time">
                                        <Typography variant="body2" color="text.secondary">
                                            {(currentItem.product.defaultShipmentMethod?.duration || 0) + (currentItem.product.processingDuration || 0)}
                                        </Typography>
                                    </Tooltip>

                                    <EditableDateCell
                                        value={eta}
                                        onValueChange={updateEta}
                                        minDate={currentItem.productionStartDate}
                                    />
                                </Stack>
                            </ListItem>
                        </List>
                    </Box>

                    {recommendation && shipment?.processedDate && (
                        <Stack spacing={2}>
                            <Typography variant="subtitle1" fontWeight="medium">
                                Forecast Data
                            </Typography>

                            {/* Waterfall Chart */}
                            <Box
                                sx={{
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    p: 2,
                                    bgcolor: theme.palette.background.default,
                                }}
                            >
                                <ForecastWaterfallChart recommendation={recommendation} processedDate={shipment?.processedDate || new Date()} />
                            </Box>

                        </Stack>
                    )}



                    {/* Change Shipment */}
                    {availableShipments.length > 0 && (
                        <Stack spacing={2}>
                            <Typography variant="subtitle1" fontWeight="medium">
                                Shipment
                            </Typography>
                            <Stack spacing={1}>
                                {availableShipments.map((availableShipment) => (
                                    <Stack key={availableShipment.id} direction="row" alignItems="center" gap={1} border={1} borderColor="divider" borderRadius={1} p={1}>

                                        <ShipmentPreview shipment={availableShipment} />


                                        <Stack borderLeft={1} borderColor="divider" px={1} width="300px" alignItems="flex-end">
                                            {selectedShipment?.id === availableShipment.id ? (
                                                <Chip label="Selected shipment" color="primary" />
                                            ) : (
                                                <Button variant="text" size="small" onClick={() => handleShipmentChange(availableShipment)} sx={{ whiteSpace: 'nowrap' }}>Use this shipment</Button>
                                            )}
                                        </Stack>
                                    </Stack>
                                ))}
                            </Stack>
                        </Stack>
                    )}



                </Stack>


            </DialogContent >

            {/* Bottom Action Bar */}
            < Box sx={{
                py: 3,
                px: 4,
                borderTop: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper'
            }
            }>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Button
                        variant="text"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleRemove}
                    >
                        Remove from purchase order
                    </Button>

                    <Button
                        variant="contained"
                        onClick={onClose}
                    >
                        Close
                    </Button>
                </Stack>
            </Box >
        </>
    );
}; 